import { SuitcaseRollingIcon } from '@phosphor-icons/react';
import { AirplaneSeatIcon } from '@voll-intelligence/ui-kit/icons/airplane-seat';
import type { TripStop } from './derive.js';
import { t } from './i18n.js';
import type {
  ItineraryAddon,
  ItineraryAddonState,
  ItineraryAddons,
  ItineraryDirection,
} from './itinerary_addons.js';
import { statedBaggageLabel } from './labels.js';
import { isChangeInProgress } from './rebooked_stop.js';
import { isPartyStop } from './stop_party.js';

/** Os chips de lacuna no pé do card do voo EMITIDO ("lacuna é oferta, nunca
 *  portão"): a pendente é oferta em bege com verbo; a RESOLVIDA vira chip quieto
 *  com o valor. A lacuna segue o tri-estado do host: `offer` abre o fluxo do
 *  addon, `requested` conta o pedido, e sem nenhum dos dois não há chip — nunca
 *  um pedido escrito ao agente, que não resolve assento nem bagagem (#4053). */
export function StopGaps({
  stop,
  addons,
  authenticated,
}: Readonly<{
  stop: TripStop;
  addons?: ItineraryAddons;
  authenticated: boolean;
}>) {
  const leg = stop.leg;
  // Só VOO: o ônibus também tem perna em `tripStops`, e assento/bagagem extra
  // são addons do bilhete aéreo — ofertá-los num trecho rodoviário mentiria.
  if (!leg || stop.service.modal !== 'FLIGHT' || stop.service.status !== 'CONFIRMED') return null;
  // O voo que está sendo trocado não recebe assento nem bagagem: o novo é que vai voar.
  if (isChangeInProgress(stop.service)) return null;
  // Assento e bagagem são de CADA passageiro: no card de vários, moram no voucher de cada um.
  if (isPartyStop(stop)) return null;
  const key = stop.service.key;
  // A perna "volta" é o retorno; a ida (e a perna sem rótulo) é a saída.
  const direction: ItineraryDirection = leg.leg === 'volta' ? 'return' : 'outbound';
  const baggage = statedBaggageLabel(leg);
  const gap = (addon: ItineraryAddon) => (
    <GapChip
      addon={addon}
      state={addons?.state(key, direction, addon) ?? null}
      onOpen={() => {
        addons?.open(key, addon, direction);
      }}
      authenticated={authenticated}
    />
  );
  return (
    <span className="tp-stop__gaps">
      {leg.seat ? (
        <span className="tp-stop__gap">
          <AirplaneSeatIcon size={16} aria-hidden />
          {leg.seat}
        </span>
      ) : (
        gap('seat')
      )}
      {baggage && (
        <span className="tp-stop__gap">
          <SuitcaseRollingIcon size={16} aria-hidden />
          {baggage}
        </span>
      )}
      {/* A franquia não esconde a mala extra já pedida: o andamento fica ao lado dela. */}
      {(!baggage || addons?.state(key, direction, 'baggage')?.kind === 'requested') &&
        gap('baggage')}
    </span>
  );
}

/** Uma lacuna do voo: o pedido já feito (a pílula com o andamento, em perigo quando
 *  falhou) ou a oferta "Adicionar" que abre o fluxo (#4015) — o nome acessível diz O QUE
 *  se adiciona, o glifo diz o mesmo a quem vê. Sem estado do host, nada. */
function GapChip({
  addon,
  state,
  onOpen,
  authenticated,
}: Readonly<{
  addon: ItineraryAddon;
  state: ItineraryAddonState | null;
  onOpen: () => void;
  authenticated: boolean;
}>) {
  const Icon = addon === 'seat' ? AirplaneSeatIcon : SuitcaseRollingIcon;
  if (state?.kind === 'requested') {
    return (
      <span
        className={state.tone === 'failed' ? 'tp-stop__gap tp-stop__gap--failed' : 'tp-stop__gap'}
      >
        <Icon size={16} aria-hidden />
        {state.label}
      </span>
    );
  }
  if (state?.kind !== 'offer') return null;
  return (
    <button
      type="button"
      className="tp-stop__gap"
      aria-label={t(addon === 'seat' ? 'trips.sugg.seatChip' : 'trips.sugg.bagChip')}
      disabled={!authenticated}
      onClick={onOpen}
    >
      <Icon size={16} aria-hidden />
      {t('trips.sugg.addChip')}
    </button>
  );
}
